import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'
import { AssessmentsTable } from '@/features/assessments'
import {
  useAssessmentsForEmployee,
  useConvexCreateAssessment,
} from '@/features/assessments/services/assessments.convex'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useMyConvexOrganizationId, useMyConvexUserId } from '@/features/auth'
import { useConfirmDialog } from '@/contexts/confirm-dialog-context'
import { PeriodConfigRequiredBanner } from '@/features/organization'
import { usePeriodReadiness } from '@/features/organization/services/period-config.convex'

const PAGE_SIZE = 10

export const Route = createFileRoute(
  '/(dashboard)/direct-reports/$directReportId/assessments/',
)({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      name: (search.name as string) || undefined,
    }
  },
  beforeLoad: ({ params, search }) => ({
    breadcrumbs: [
      { title: 'Direct Reports', href: '/direct-reports' },
      { title: search.name || params.directReportId },
      { title: 'Assessments' },
    ],
  }),
})

function RouteComponent() {
  const { directReportId } = Route.useParams()
  const [isCreating, setIsCreating] = useState(false)

  const convexOrgId = useMyConvexOrganizationId()
  const convexUserId = useMyConvexUserId()
  const { showConfirmDialog } = useConfirmDialog()
  const readiness = usePeriodReadiness(convexOrgId)

  const periodDisabled =
    readiness.status !== 'ready' && readiness.status !== 'loading'
  const periodDisabledReason =
    readiness.status === 'not-configured'
      ? 'Configure assessment periods first in organization settings.'
      : readiness.status === 'awaiting-start'
        ? `Assessments unlock on ${new Date(readiness.startDate).toLocaleDateString()}.`
        : readiness.status === 'no-current-period'
          ? 'No active assessment period — open organization settings to generate one.'
          : null

  const { results, status, loadMore } =
    useAssessmentsForEmployee(directReportId)
  const createAssessmentMutation = useConvexCreateAssessment()

  const isLoading = status === 'LoadingFirstPage'
  const hasMore = status === 'CanLoadMore'
  const isLoadingMore = status === 'LoadingMore'

  // Disable create button if any assessment has an incomplete response
  const hasPendingAssessment = results.some((a) =>
    a.responses.some((r) => r.completedAt === null),
  )

  const handleCreateAssessment = () => {
    showConfirmDialog({
      title: 'Create Assessment',
      description:
        'Are you sure you want to create a new assessment for this direct report?',
      confirmText: 'Create Assessment',
      cancelText: 'Cancel',
      onConfirm: async () => {
        if (!convexOrgId) return
        setIsCreating(true)
        try {
          await createAssessmentMutation.mutateAsync({
            employeeUserId: directReportId,
            organizationId: convexOrgId,
          })
          toast.success('Assessment created successfully!')
        } finally {
          setIsCreating(false)
        }
      },
    })
  }

  return (
    <div className="space-y-6 mt-2">
      <PeriodConfigRequiredBanner />
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 p-6 bg-muted/30 rounded-lg">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Assessments</h2>
          <p className="text-sm text-muted-foreground">
            View and manage assessments for this direct report
          </p>
        </div>
        {/* Only show Create Assessment button if the viewer is not the direct report */}
        {convexUserId !== directReportId && (
          <div className="flex items-center gap-2">
            {periodDisabled && periodDisabledReason ? (
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-block">
                      <Button disabled>Create Assessment</Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{periodDisabledReason}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <Button
                onClick={handleCreateAssessment}
                disabled={hasPendingAssessment || isCreating}
              >
                {isCreating ? 'Creating...' : 'Create Assessment'}
              </Button>
            )}
          </div>
        )}
      </div>

      <AssessmentsTable
        assessments={results}
        currentUserId={convexUserId ?? ''}
        isLoading={isLoading}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        onLoadMore={() => loadMore(PAGE_SIZE)}
        isDirectReportsView={true}
      />
    </div>
  )
}
